import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Link as LinkIcon, Pencil, Trash, Wallet, LockKey, HandCoins } from "@phosphor-icons/react";
import { ScriptStatusBadge } from "@/components/StatusBadge";
import RequestPaymentDialog from "@/components/RequestPaymentDialog";
import { SortHead, PlatformBadge, MarginPill, useSortedList, fmtInt, fmtCur } from "./utils";
import { LiveCell } from "./LiveCell";

export function LockedTable({ list, onEdit, onDelete, onPay, onCreatorUpdated, campaignType }) {
  const { sorted, sort, toggle } = useSortedList(list);
  const [requestFor, setRequestFor] = useState(null);
  if (list.length === 0) {
    return (
      <Card className="p-12 text-center border border-dashed border-slate-300 shadow-none">
        <LockKey size={28} className="mx-auto text-slate-300 mb-2" weight="duotone" />
        <div className="text-sm text-slate-500">
          No locked creators yet. Change a prospect's status to <b>Locked</b> once the brand confirms.
        </div>
      </Card>
    );
  }
  return (
    <Card className="border border-slate-200 shadow-none overflow-hidden">
      <div className="overflow-auto max-h-[70vh]">
        <Table>
          <TableHeader className="bg-slate-50 sticky top-0 z-10 shadow-[0_1px_0_0_rgba(0,0,0,0.06)]">
            <TableRow>
              <SortHead label="Creator" k="username" sort={sort} onToggle={toggle} />
              <SortHead label="City" k="city" sort={sort} onToggle={toggle} />
              <SortHead label="Language" k="language" sort={sort} onToggle={toggle} />
              <TableHead>Script status</TableHead>
              <TableHead>Deliverables</TableHead>
              <SortHead label="Final cost" k="final_cost" sort={sort} onToggle={toggle} className="text-right" />
              {campaignType === "Creator Based" && (
                <>
                  <SortHead label="Internal" k="internal_cost" sort={sort} onToggle={toggle} className="text-right" />
                  <SortHead label="Client" k="client_cost" sort={sort} onToggle={toggle} className="text-right" />
                  <TableHead className="text-right">Margin</TableHead>
                </>
              )}
              <SortHead label="Notes" k="notes" sort={sort} onToggle={toggle} className="min-w-[200px]" />
              <TableHead className="text-right w-[140px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((c) => (
              <TableRow key={c.id} className="hover:bg-slate-50/60" data-testid={`locked-row-${c.id}`}>
                <TableCell>
                  {c.link ? (
                    <a
                      href={c.link}
                      target="_blank"
                      rel="noreferrer"
                      className="font-medium text-slate-900 hover:text-blue-600 hover:underline inline-flex items-center gap-1"
                      data-testid={`open-creator-${c.id}`}
                    >
                      @{c.username}{c.platform ? <PlatformBadge p={c.platform} /> : null}
                      <LinkIcon size={11} className="opacity-50" />
                    </a>
                  ) : (
                    <div className="font-medium text-slate-900" data-testid={`open-creator-${c.id}`}>@{c.username}{c.platform ? <PlatformBadge p={c.platform} /> : null}</div>
                  )}
                  <div className="text-xs text-slate-500">{fmtInt(c.followers)} followers</div>
                </TableCell>
                <TableCell className="text-slate-700 text-xs">{c.city || <span className="text-slate-400">—</span>}</TableCell>
                <TableCell className="text-slate-700 text-xs">{c.language || <span className="text-slate-400">—</span>}</TableCell>
                <TableCell><ScriptStatusBadge value={c.script_status} /></TableCell>
                <TableCell className="text-xs text-slate-700 max-w-[220px]">
                  <div className="line-clamp-2" title={c.deliverables}>{c.deliverables || "—"}</div>
                </TableCell>
                <TableCell className="text-right font-mono text-xs font-semibold text-slate-900">{fmtCur(c.final_cost)}</TableCell>
                {campaignType === "Creator Based" && (
                  <>
                    <TableCell className="text-right font-mono text-xs text-slate-700" data-testid={`locked-internal-${c.id}`}>{fmtCur(c.internal_cost)}</TableCell>
                    <TableCell className="text-right font-mono text-xs text-slate-700" data-testid={`locked-client-${c.id}`}>{fmtCur(c.client_cost)}</TableCell>
                    <TableCell className="text-right font-mono text-xs font-semibold" data-testid={`locked-margin-${c.id}`}>
                      <MarginPill internal={c.internal_cost} client={c.client_cost} />
                    </TableCell>
                  </>
                )}
                <TableCell className="min-w-[200px]">
                  <LiveCell creator={c} field="notes" type="text" onUpdated={onCreatorUpdated} />
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex gap-1">
                    <button
                      onClick={() => onPay(c)}
                      className="p-1.5 text-emerald-600 hover:text-white hover:bg-emerald-600 rounded transition-colors"
                      title="Log payment"
                      data-testid={`pay-${c.id}`}
                    >
                      <Wallet size={14} />
                    </button>
                    <button
                      onClick={() => setRequestFor(c)}
                      className="p-1.5 text-amber-600 hover:text-white hover:bg-amber-600 rounded transition-colors"
                      title="Request payment"
                      data-testid={`request-pay-${c.id}`}
                    >
                      <HandCoins size={14} />
                    </button>
                    <button onClick={() => onEdit(c)} className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded" data-testid={`edit-${c.id}`}>
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => onDelete(c)} className="p-1.5 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded" data-testid={`delete-${c.id}`}>
                      <Trash size={14} />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <RequestPaymentDialog open={!!requestFor} onOpenChange={(v) => !v && setRequestFor(null)} creator={requestFor} onCreated={() => setRequestFor(null)} />
    </Card>
  );
}
